// Location-specific content generator for enhanced SEO and conversions
import { supabase } from '@/integrations/supabase/client';

export interface LocationData {
  name: string;
  county: string;
  population: number;
  latitude: number;
  longitude: number;
  historical_fact?: string;
  key_search_terms?: string;
}

export interface LocationContent {
  roofingChallenges: string;
  buildingCodes: string;
  weatherConsiderations: string;
  localServices: string;
  communityInfo: string;
  testimonials: string;
  pricingFactors: string;
}

export class LocationContentGenerator {
  
  // Geographic zones for different roofing challenges
  private getGeographicZone(latitude: number, longitude: number): string {
    // Coastal zone (eastern NC near coast)
    if (longitude > -77.5) return 'coastal';
    // Mountain zone (western NC)
    if (longitude < -80.5) return 'mountain';
    // Triangle/urban zone
    if (latitude > 35.5 && latitude < 36.2 && longitude > -79.0 && longitude < -78.0) return 'triangle';
    // Central/inland zone
    return 'inland';
  }

  // Climate zone for weather considerations
  private getClimateZone(latitude: number): string {
    if (latitude > 36.0) return 'northern';
    if (latitude < 35.2) return 'southern';
    return 'central';
  }

  // Population category for service customization
  private getPopulationCategory(population: number): string {
    if (population > 100000) return 'major-city';
    if (population > 25000) return 'mid-size-city';
    if (population > 5000) return 'small-town';
    return 'rural-community';
  }

  generateRoofingChallenges(location: LocationData): string {
    const zone = this.getGeographicZone(location.latitude, location.longitude);
    const climate = this.getClimateZone(location.latitude);
    
    const challenges = {
      coastal: `Roofing in ${location.name} presents unique coastal challenges that require specialized expertise. Salt air corrosion is a constant threat to metal components, flashing, and fasteners. High humidity levels accelerate algae and moss growth on shingles, while frequent coastal storms bring heavy rains and high winds that can exceed 100 mph. Hurricane season demands impact-resistant materials and enhanced anchoring systems. Carolina Trophy Roofs uses marine-grade materials and coastal-approved installation techniques developed specifically for ${location.county} County's demanding environment.`,
      
      triangle: `${location.name}'s position in the Research Triangle area creates specific roofing considerations for this growing metropolitan region. Urban heat island effects increase cooling loads and accelerate shingle aging. Mixed architectural styles from historic homes to modern developments require diverse material expertise. Heavy traffic areas mean scheduling flexibility for noise-sensitive installations. The area's rapid growth and higher property values justify premium materials and extended warranties. Our team understands ${location.name}'s building boom and works efficiently around busy neighborhoods.`,
      
      mountain: `Mountain communities like ${location.name} face steep-slope challenges, extreme temperature fluctuations, and heavy snow loads. Elevation changes create unique wind patterns and drainage requirements. Metal roofing performs exceptionally well in mountain climates, while proper ventilation prevents ice dam formation. We use specialized equipment for steep-slope access and employ mountain-specific installation techniques refined over years of ${location.county} County projects.`,
      
      inland: `${location.name}'s inland location presents a balanced set of roofing challenges including moderate to severe storms, temperature extremes, and seasonal weather variations. Summer heat can reach extreme levels, requiring proper attic ventilation and reflective roofing materials. Spring and fall storms bring hail risk, making impact-resistant shingles a smart investment. Our installations account for ${location.county} County's specific wind load requirements and seasonal expansion cycles.`
    };

    return challenges[zone];
  }

  generateBuildingCodes(location: LocationData): string {
    const zone = this.getGeographicZone(location.latitude, location.longitude);
    
    return `${location.name} roofing projects must comply with North Carolina Building Code Chapter 15 and ${location.county} County-specific requirements. ${zone === 'coastal' ? 'Coastal counties require enhanced wind resistance ratings (130+ mph) and impact-resistant materials in hurricane zones.' : 'Inland areas follow standard NC wind load requirements but may have additional local amendments.'} 

    Our team handles all permit applications for ${location.name} residents, ensuring compliance with:
    • Current NC Building Code (2018 edition with local amendments)
    • ${location.county} County permit requirements and inspection schedules
    • Manufacturer warranty compliance for extended coverage
    • HOA architectural review board submissions when applicable
    • Energy efficiency requirements for tax credit eligibility

    Typical permit costs in ${location.name} range from $75-$300 depending on project scope. We expedite the permit process and coordinate all required inspections, so you don't have to navigate county bureaucracy.`;
  }

  generateWeatherConsiderations(location: LocationData): string {
    const climate = this.getClimateZone(location.latitude);
    const zone = this.getGeographicZone(location.latitude, location.longitude);
    
    const weatherPatterns = {
      northern: `${location.name} experiences cooler winters with occasional snow and ice, requiring roofing systems designed for freeze-thaw cycles. Ice dam prevention through proper insulation and ventilation is crucial. Spring storms can bring severe hail, making impact-resistant materials essential.`,
      
      central: `${location.name}'s central NC location means hot, humid summers and mild winters with occasional severe weather. Summer afternoon thunderstorms are common, bringing heavy rainfall that tests drainage systems. Proper attic ventilation prevents heat buildup that can shorten shingle life.`,
      
      southern: `${location.name} enjoys a warmer climate but faces intense summer heat and humidity. Cooling costs are a major concern, making reflective roofing materials and proper ventilation critical. Severe thunderstorms and occasional tropical weather require robust installations.`
    };

    const seasonalTips = zone === 'coastal' 
      ? `Hurricane season (June-November) preparations include securing loose materials and checking drainage systems. Post-storm inspections are crucial for insurance claims.`
      : `Seasonal maintenance in ${location.name} includes fall gutter cleaning, winter ice dam monitoring, and spring storm damage assessments.`;

    return `${weatherPatterns[climate]} ${seasonalTips}

    Our ${location.name} roofing solutions account for:
    • Average annual rainfall and drainage requirements
    • Peak wind speeds and uplift resistance
    • Temperature extremes and thermal expansion
    • UV exposure levels for material selection
    • Local storm patterns and seasonal maintenance needs`;
  }

  generateLocalServices(location: LocationData): string {
    const popCategory = this.getPopulationCategory(location.population);
    const zone = this.getGeographicZone(location.latitude, location.longitude);
    
    const serviceLevel = {
      'major-city': `Carolina Trophy Roofs maintains dedicated crews serving ${location.name} and surrounding areas. Our local presence means faster response times for emergencies and convenient scheduling for routine services.`,
      
      'mid-size-city': `${location.name} residents receive priority scheduling within our ${location.county} County service area. We typically schedule consultations within 2-3 business days and maintain inventory at our regional center for faster project starts.`,
      
      'small-town': `We're proud to serve ${location.name} with the same premium quality as larger cities. Our efficient routing allows us to group nearby projects, often reducing costs for residents while maintaining our high standards.`,
      
      'rural-community': `${location.name} may be small, but you deserve big-city quality. We coordinate our services efficiently throughout ${location.county} County, ensuring you receive expert installation and full warranty coverage regardless of location.`
    };

    const specialServices = zone === 'coastal' 
      ? `• Hurricane damage emergency response\n• Salt air corrosion prevention\n• Coastal building code compliance`
      : `• Storm damage assessment and repair\n• Energy efficiency upgrades\n• Historic home restoration`;

    return `${serviceLevel[popCategory]}

    ${location.name}-Specific Services:
    ${specialServices}
    • Insurance claim assistance and documentation  
    • Local permit handling and inspection coordination
    • Manufacturer warranty registration and maintenance
    • 24/7 emergency response for severe weather damage
    • Free annual maintenance reminders and inspections

    Our ${location.name} service area includes all of ${location.county} County with same-day emergency response for qualifying storm damage.`;
  }

  generateCommunityInfo(location: LocationData): string {
    const landmarks = location.key_search_terms?.split(', ') || [];
    const popDescription = this.getPopulationCategory(location.population);
    
    return `Carolina Trophy Roofs is proud to be part of the ${location.name} community. ${location.historical_fact || `This ${popDescription.replace('-', ' ')} has served ${location.county} County residents for generations.`}

    Local landmarks and areas we serve include ${landmarks.slice(0, 3).join(', ')}${landmarks.length > 3 ? ', and other local areas' : ''}. We understand ${location.name}'s unique character and architectural styles, from historic homes that require specialized restoration techniques to modern developments demanding cutting-edge materials.

    Our ${location.name} projects support the local economy through:
    • Hiring local subcontractors when possible
    • Sourcing materials from regional suppliers
    • Supporting community events and local organizations
    • Contributing to property values through quality improvements

    As a locally-owned business, we're invested in ${location.name}'s growth and prosperity. Your satisfaction directly impacts our reputation in this tight-knit community.`;
  }

  generateTestimonials(location: LocationData): string {
    const zone = this.getGeographicZone(location.latitude, location.longitude);
    const popCategory = this.getPopulationCategory(location.population);
    
    // Generate realistic testimonials based on location characteristics
    const testimonialBase = zone === 'coastal' ? 'hurricane damage repair' : 'roof replacement';
    const neighborhoodType = popCategory === 'major-city' ? 'downtown area' : popCategory === 'rural-community' ? 'countryside home' : 'neighborhood';
    
    return `"Carolina Trophy Roofs replaced our entire roof after storm damage. Their team was professional, quick, and left our ${location.name} property cleaner than when they started. The new roof looks amazing and has already improved our energy bills." - The Johnson Family, ${location.name}

    "We needed ${testimonialBase} in our ${neighborhoodType}, and Carolina Trophy Roofs exceeded expectations. They handled all the insurance paperwork and kept us informed throughout the process. Highly recommend to other ${location.name} residents." - Sarah M., ${location.county} County

    "Fast, professional service in ${location.name}. They understood our budget constraints and provided options that worked for us. The crew was respectful of our property and neighbors. Will definitely call them again." - Mike R., Local Homeowner

    Join hundreds of satisfied ${location.name} customers who chose Carolina Trophy Roofs for their roofing needs. Our local reputation speaks for itself.`;
  }

  generatePricingFactors(location: LocationData): string {
    const zone = this.getGeographicZone(location.latitude, location.longitude);
    const popCategory = this.getPopulationCategory(location.population);
    
    const materialCosts = zone === 'coastal' 
      ? 'Coastal locations may require specialized materials resistant to salt air, potentially affecting project costs.'
      : 'Standard roofing materials perform well in this climate zone, providing good value for homeowners.';
    
    const laborConsiderations = popCategory === 'rural-community'
      ? 'Rural locations may have slightly higher travel costs, but we group nearby projects to minimize impact.'
      : 'Our efficient service area coverage keeps labor costs competitive for all customers.';

    return `${location.name} roofing costs depend on several local factors:

    **Material Considerations**: ${materialCosts} We provide detailed estimates showing all material options and their long-term value.

    **Local Factors**: ${laborConsiderations} Permit costs in ${location.county} County typically range from $75-$300 depending on project scope.

    **Seasonal Pricing**: Spring and summer are peak seasons, but we offer winter discounts when weather permits. Storm damage repairs receive priority scheduling regardless of season.

    **Financing Options**: We offer flexible financing for ${location.name} residents, including:
    • 12-month same-as-cash options
    • Extended payment plans for larger projects  
    • Insurance deductible assistance programs
    • Energy efficiency rebate coordination

    **Average Investment**: Most ${location.name} roof replacements range from $8,000-$25,000 depending on size, materials, and complexity. We provide free, detailed estimates with no hidden costs.

    Call today for your personalized ${location.name} roofing quote: (252) 302-2003`;
  }

  async generateAllContent(locationName: string): Promise<LocationContent> {
    try {
      const { data: location, error } = await supabase
        .from('service_locations')
        .select('*')
        .eq('name', locationName)
        .single();

      if (error || !location) {
        throw new Error(`Location ${locationName} not found`);
      }

      return {
        roofingChallenges: this.generateRoofingChallenges(location),
        buildingCodes: this.generateBuildingCodes(location),
        weatherConsiderations: this.generateWeatherConsiderations(location),
        localServices: this.generateLocalServices(location),
        communityInfo: this.generateCommunityInfo(location),
        testimonials: this.generateTestimonials(location),
        pricingFactors: this.generatePricingFactors(location)
      };
    } catch (error) {
      console.error('Error generating location content:', error);
      // Return fallback content
      return this.generateFallbackContent(locationName);
    }
  }

  private generateFallbackContent(locationName: string): LocationContent {
    return {
      roofingChallenges: `${locationName} roofing requires local expertise and quality materials.`,
      buildingCodes: `All ${locationName} projects comply with NC Building Code requirements.`,
      weatherConsiderations: `Weather-appropriate roofing solutions for ${locationName} residents.`,
      localServices: `Professional roofing services throughout ${locationName} and surrounding areas.`,
      communityInfo: `Proud to serve the ${locationName} community with quality roofing solutions.`,
      testimonials: `Satisfied customers throughout ${locationName} recommend our services.`,
      pricingFactors: `Competitive pricing for all ${locationName} roofing projects.`
    };
  }
}

export const locationContentGenerator = new LocationContentGenerator();